import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Reusable SEO Helmet component.
 * Always injects:
 *   - <html lang>
 *   - <title>, <meta description/keywords>
 *   - Open Graph tags
 *   - <link rel="canonical">
 *   - hreflang alternates (ru, en, x-default)
 *
 * Use on every public page.
 */
const SeoHelmet = ({ title, description, keywords = '', path, jsonLd }) => {
  const { currentLanguage } = useLanguage();
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const pageUrl = `${baseUrl}${path || (typeof window !== 'undefined' ? window.location.pathname : '/')}`;
  const ogLocale = currentLanguage === 'EN' ? 'en_US' : 'ru_RU';
  const altLocale = currentLanguage === 'EN' ? 'ru_RU' : 'en_US';
  const htmlLang = currentLanguage === 'EN' ? 'en' : 'ru';

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={altLocale} />
      <link rel="canonical" href={pageUrl} />
      <link rel="alternate" hrefLang="ru" href={pageUrl} />
      <link rel="alternate" hrefLang="en" href={pageUrl} />
      <link rel="alternate" hrefLang="x-default" href={pageUrl} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SeoHelmet;
